import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import AddressManager from "@components/AddressManager";
import { getCasesByUser, searchUserById, updateCaseAddress } from "@store/admin/adminAction";
import { userObject } from "@store/user/userSlice";
import { Case } from "@types-runway/case";
import { RootState, UserSearchData } from "@types-runway/state";
import { User } from "@types-runway/user";

import { UserDTO } from "../DTOs/UserDTO";

type CaseAddressProps = {
  case_: Case;
  closeModal: () => void;
};

const CaseAddress = ({ case_, closeModal }: CaseAddressProps) => {
  const userData = useSelector<RootState, UserSearchData>((state) => state.admin.users);
  const isUpdatingAddress = useSelector<RootState, boolean>(
    (state) => state.admin.isUpdatingAddress
  );
  const [userResult, setUserResult] = useState<User>(userObject.data);
  const wasUpdating = useRef(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isUpdatingAddress) {
      wasUpdating.current = true;
    } else if (wasUpdating.current) {
      wasUpdating.current = false;
      closeModal();
    }
  }, [isUpdatingAddress, closeModal]);

  const setCaseShippingAddress = (addressId: string) => {
    // Reassign the address and (if the case has an order) re-send it to the
    // pharmacy in one sequenced saga: the re-send happens only after the address
    // PUT succeeds, so the pharmacy always gets the new address. Replaces the
    // former dispatch + setTimeout(1000) race (ENG-472).
    dispatch(
      updateCaseAddress({
        caseId: case_.id,
        addressId,
        orderId: case_.CurexaOrder?.id,
        referenceId: case_.CurexaOrder?.order_id,
      })
    );
  };

  useEffect(() => {
    // Get user's cases
    dispatch(getCasesByUser(case_.UserId));

    // Always search user on CaseAddress mount
    dispatch(searchUserById({ id: case_.UserId }));

    if (userData.id !== "") {
      const userModel = new UserDTO(userData);
      setUserResult(userModel);
    }
  }, [userData.id]);

  return (
    <>
      <h2>Case: {case_.caseId}</h2>
      <hr></hr>
      <h3>Selected Shipping Address</h3>
      <hr></hr>
      <p>
        <b>Street: </b>
        {case_.shippingAddress?.street}
      </p>
      <p>
        <b>Secondary:</b> {case_.shippingAddress?.secondary}
      </p>
      <p>
        <b>City: </b>
        {case_.shippingAddress?.city}
      </p>
      <p>
        <b>State: </b>
        {case_.shippingAddress?.state}
      </p>
      <p>
        <b>Postal Code: </b>
        {case_.shippingAddress?.postalCode}
      </p>
      <p>
        <b>Country:</b> {case_.shippingAddress?.country}
      </p>
      <hr></hr>
      {userResult.id !== "" && (
        <AddressManager
          user={{
            isPending: false,
            data: userResult,
            error: {},
            success: { type: null },
          }}
          openForm={false}
          confirmSelectedAddress={setCaseShippingAddress}
          isCheckout
        />
      )}
    </>
  );
};

export default CaseAddress;
